import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ConversationList } from '@up2d8/contracts';
import { useSession } from '../../shared/session';

/** Boven dit aantal staat er "9+": zoals bij de bel. */
const MAX_SHOWN = 9;

/**
 * Het berichtenicoon in de kopbalk, met het aantal gesprekken dat op je wacht (Stap 34c).
 *
 * Tot deze stap stond dit icoon er bewust niet, ook al had de UI-mockup het: een icoon dat niets
 * doet, is erger dan een ontbrekend icoon (ADR-0050).
 *
 * Verdwijnt volledig als de module `messaging` uit staat bij een bedrijf. Dat merkt de client aan
 * een `404` op de gesprekkenlijst — dat is wat `@RequireFeature` oplevert, en het is bewust iets
 * anders dan een `403`: een uitgezette functie bestaat niet.
 */
/**
 * Hoeveel gesprekken op je wachten, of `available: false` als `messaging` uit staat.
 *
 * Gedeeld door het icoon op de telefoon en de kopbalk en zijbalk op desktop: dezelfde querysleutel,
 * dus één verzoek per halve minuut, op elk scherm - niet alleen op het berichtenscherm.
 */
export function useUnreadConversations() {
  const { api, has } = useSession();

  return useQuery({
    queryKey: ['conversations', 'unread'],
    enabled: has('messaging.conversation.use'),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    // Niet opnieuw proberen bij een 404: staat de module uit, dan blijft dat zo zolang de sessie
    // duurt, en elke halve minuut opnieuw vragen is dan zinloos verkeer.
    retry: false,
    queryFn: async (): Promise<{ available: boolean; unread: number }> => {
      const { data, error, response } = await api.GET('/api/v1/conversations');
      if (error || !data) {
        return { available: response?.status !== 404, unread: 0 };
      }
      return { available: true, unread: (data as ConversationList).totalUnread };
    },
  });
}

export function MessagesIcon() {
  const { t } = useTranslation();
  const { has } = useSession();
  const conversations = useUnreadConversations();

  if (!has('messaging.conversation.use')) return null;
  if (conversations.data?.available === false) return null;

  const unread = conversations.data?.unread ?? 0;

  return (
    <Link
      to="/app/gesprekken"
      data-touch-target=""
      data-testid="messages-icon"
      aria-label={
        unread > 0 ? t('messaging.iconWithCount', { count: unread }) : t('messaging.icon')
      }
      className="relative inline-flex items-center justify-center rounded-pill p-2 text-text-secondary hover:bg-surface hover:text-brand-primary"
    >
      <MessageSquare aria-hidden className="h-6 w-6" />
      {unread > 0 && (
        <span
          // `aria-hidden`: het aantal staat al in het label van de link.
          aria-hidden
          className="absolute right-0 top-0 min-w-[1.25rem] rounded-pill bg-brand-orange px-1 text-center text-caption font-semibold text-text-on-dark"
        >
          {unread > MAX_SHOWN ? `${MAX_SHOWN}+` : unread}
        </span>
      )}
    </Link>
  );
}
